import * as React from "react"
import Skeleton from "react-loading-skeleton"
import ToolTip from "../../General/ToolTip"
import tableStyles from "scss/TeacherAssignments.module.scss"
import { AssignmentResponse } from "shared/types/teacherTypes"
import { formatToLongDate } from "helpers/dateHelper"
import styles from "../General/general.module.scss"
import { ReactTable } from "@stem-sims/nexus"
import { AuthContext } from "AuthContext"
import { StudentAssignmentResponse } from "shared/types/studentTypes"
import HideCompletedToggle from "../Student/components/HideCompletedToggle"
import { isStudentAssignments } from "shared/typeGuards/isStudentAssignments"
import { isStudentAssignment } from "shared/typeGuards/isStudentAssignment"

interface AssignmentsProps {
    assignments: AssignmentResponse[] | StudentAssignmentResponse[]
    loading: boolean
    onRowClick: (row) => void
    changeBackgroundColor: (row) => string
    extraTeacherComponents: {component: React.FC<{onClick: () => void}>, prop: () => void}[]
    rowTooltip: string
    extraCols: unknown[]
    origin: "stemsims" | "sci35"
    checkboxCol?: unknown
}

export default function Assignments({ origin, assignments, loading, onRowClick, changeBackgroundColor, rowTooltip, extraCols, checkboxCol, extraTeacherComponents }: AssignmentsProps) {

    const { isStudent } = React.useContext(AuthContext)

    const [filteredAssignments, setFilteredAssignments] = React.useState<StudentAssignmentResponse[]>([])

    const columns: unknown[] = React.useMemo(() => addColumns({ origin, extraCols, rowTooltip, isStudent, checkboxCol }), [origin, extraCols, rowTooltip, isStudent, checkboxCol])

    const onHideCompletedAssignments = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (!isStudentAssignments(isStudent, assignments)) return

        const show = e.currentTarget.checked === true
        if (show) {
            setFilteredAssignments(assignments)
        } else {
            const incompleteAssignments = assignments.filter(a => a.status === "Rejected" || a.status === "Not Submitted" || a.status === "Incomplete" || a.status === "Rework Required" || a.status === "Reworking")
            setFilteredAssignments(incompleteAssignments)
        }
    }, [assignments, isStudent])

    const extraStudentComponents = [{component: HideCompletedToggle, prop: onHideCompletedAssignments}]

    React.useEffect(() => {
        if (!assignments || assignments.length === 0 || !isStudentAssignments(isStudent, assignments)) return

        const incompleteAssignments = assignments.filter(a => a.status === "Rejected" || a.status === "Not Submitted" || a.status === "Incomplete" || a.status === "Rework Required" || a.status === "Reworking")
        setFilteredAssignments(incompleteAssignments)
    }, [assignments, isStudent])

    return (<>
        {loading ? <div className="mt-5">
            <Skeleton height={40} count={6} />
        </div>
        : 
        <div className={`${tableStyles.reactTable} class-assignments`}>
            <ReactTable columns={columns} data={isStudent ? filteredAssignments : assignments} extraComponents={isStudent ? extraStudentComponents : extraTeacherComponents} clickRow={onRowClick} customClass={changeBackgroundColor} sortBy={[{ id: 'dueDate' }]} bulkActions={[]} />
        </div>
        }
    </>)
}

function addColumns({ origin, extraCols, rowTooltip, isStudent, checkboxCol }: { origin: "stemsims" | "sci35", extraCols: unknown[], rowTooltip: string, isStudent: boolean, checkboxCol?: unknown }) {

    const dateStringSort = (rowA, rowB) => {
        const dateAValue = rowA.values.dueDate
        const dateBValue = rowB.values.dueDate
        if (dateAValue === 'N/A' && dateBValue === 'N/A') return 0
        if (dateAValue === 'N/A') return 1
        if (dateBValue === 'N/A') return -1

        const dateA = new Date(dateAValue)
        const dateB = new Date(dateBValue)
        const diff = dateA.getTime() - dateB.getTime()

        return diff > 0 ? 1 : diff < 0 ? -1 : 0
    }

    const columns: unknown[] = [
        {
            Header: 'Title',
            minWidth: 225,
            accessor: 'title',
            Cell: ({ row }) => {
                const assignment: AssignmentResponse | StudentAssignmentResponse = row.original

                const topicType = !!assignment.topicId
                const lessonType = !!assignment.lessonId
                const reviewType = !!assignment.review && assignment.lessonVariety === "Review"

                const useGrade35Version = !!(assignment.review?.grade3lessonId || assignment.review?.grade3postTestId || assignment.review?.grade3pretestId)
                const studentSpecificAssignment = isStudentAssignment(isStudent, assignment) ? false : assignment?.students?.length > 0
                return <ToolTip title={rowTooltip}>
                    <div className="text-start d-flex align-items-center justify-content-between">
                        <span className="col-4" data-testid={assignment.title}>{assignment.title}</span>
                        {reviewType && useGrade35Version && <span className={`badge ${assignment.useGrade3 ? "bg-success" : "bg-primary"} p-2`}>Grade {assignment.useGrade3 ? "3" : "5"} Version</span>}
                        <div className="d-flex align-items-center">
                            <span className={`rounded px-2 ${topicType ? styles.labelTagTopic : lessonType ? styles.labelTagLesson : styles.labelTagAssessment}`}>
                                {topicType ? "topic" : (lessonType ? "lesson" : (assignment.lessonVariety === "Pure Inquiry" ? "pure inquiry" : (assignment.lessonVariety === "Review" ? "review" : "assessment")))}
                            </span>
                            {!isStudent && <ToolTip title={studentSpecificAssignment ? "Student Specific" : "Class Assignment"}>
                                <div className={`ms-2 ${studentSpecificAssignment ? styles.studentTag : styles.classTag}`}>
                                    {studentSpecificAssignment ? <i className="fa-solid fa-s"></i> : <i className="fa-solid fa-c"></i>}
                                </div>
                            </ToolTip>}
                        </div>
                    </div>
                </ToolTip>
            }
        },
    ]

    if (origin === "stemsims") {
        const stemsimsColumns = [
            {
                Header: 'Module Name',
                id: 'lesson.module',
                accessor: (assignment) => assignment.moduleName ? assignment.moduleName : (assignment.lesson?.module ? assignment.lesson.module : 'N/A'),
                Cell: ({ row }) => {
                    const assignment: AssignmentResponse = row.original
                    return (<ToolTip title={rowTooltip}>
                        <div className="text-center">
                            {assignment.moduleName ?? 'N/A'}
                        </div>
                    </ToolTip>)
                },
            },
            {
                Header: 'Lesson Number',
                id: 'lesson.number',
                accessor: (assignment) => assignment.lesson?.number,
                maxWidth: 90,
                Cell: ({ row }) => {
                    return <ToolTip title={rowTooltip}>
                        <div>{row.original.lesson?.number ?? "N/A"}</div>
                    </ToolTip>
                }
            },
        ]
        columns.push(...stemsimsColumns)
    }

    columns.push({
        Header: 'Due Date',
        accessor: (data) => data.dueDate ? formatToLongDate(data.dueDate) : 'N/A',
        id: 'dueDate',
        sortType: dateStringSort
    })

    columns.push(...extraCols) // add extra columns such as Edit, Delete buttons etc

    if (checkboxCol) {
        columns.unshift(checkboxCol)
    }

    return columns
}
